import React from "react";
import { Box, Typography } from "@mui/material";
import { animated } from "@react-spring/web";
import {
  getViewerPanelPaddingSx,
  viewerPanelBodySx,
  viewerPanelEyebrowSx,
  viewerPanelHeaderSx,
  viewerPanelHeadingGroupSx,
  viewerPanelSurfaceSx,
} from "./panelStyles";

const StatusOverlays = ({
  unityBuildVersion,
  envLabelBottom,
  playerOffset,
  overlayWidth,
  guideInstructionText,
  isPhone,
  isPhonePortrait = false,
  currentGameState,
  lastUnitySelectedElementName,
  lastUnitySelectedElementId,
}) => {
  const SHOW_GAME_STATE_PANEL = false;
  const normalizedInstructionText =
    typeof guideInstructionText === "string" ? guideInstructionText.trim() : "";
  const instructionBottom = isPhonePortrait
    ? "calc(env(safe-area-inset-bottom, 0px) + var(--guide-instruction-bottom-offset-phone-portrait, 274px))"
    : `calc(${envLabelBottom} + ${isPhone ? 86 : 104}px)`;
  const instructionPanelPositionSx = isPhonePortrait
    ? {
        right:
          "calc(env(safe-area-inset-right, 0px) + var(--guide-stack-right-offset-phone-portrait, -8px))",
        left: "auto",
        transform: "none",
        maxWidth:
          "min(var(--guide-stack-width-phone-portrait, 220px), calc(100vw - var(--panel-horizontal-offset, 20px)))",
      }
    : {
        left: `calc(${playerOffset}px + 50% - ${playerOffset / 2}px)`,
        transform: "translateX(-50%)",
        maxWidth: overlayWidth,
      };
  const overlayPanelPaddingSx = getViewerPanelPaddingSx(isPhone);

  const mutedLabelSx = {
    ...viewerPanelEyebrowSx,
    opacity: 0.9,
    color: "rgba(161, 241, 224, 0.95)",
    fontWeight: 600,
  };

  return (
    <>
      {unityBuildVersion && (
        <Box
          sx={{
            position: "absolute",
            right: "calc(env(safe-area-inset-right, 0px) + 12px)",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
            color: "#ecfffb",
            ...overlayPanelPaddingSx,
            zIndex: 1003,
            pointerEvents: "none",
            textAlign: "right",
            maxWidth: isPhone ? "62vw" : "360px",
            ...viewerPanelSurfaceSx,
          }}
        >
          <Typography
            variant="caption"
            sx={{ ...mutedLabelSx, fontSize: "0.64rem", letterSpacing: "0.1em" }}
          >
            UNITY VERSION
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: '"area-extended", "area-normal", sans-serif',
              fontSize: isPhone ? "0.78rem" : "0.86rem",
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            {unityBuildVersion}
          </Typography>
        </Box>
      )}

      {normalizedInstructionText && (
        <Box
          component={animated.div}
          sx={{
            position: "absolute",
            bottom: instructionBottom,
            ...instructionPanelPositionSx,
            width: "fit-content",
            ...overlayPanelPaddingSx,
            boxSizing: "border-box",
            opacity: 1,
            zIndex: isPhonePortrait ? 1001 : 26,
            pointerEvents: "none",
            textAlign: "left",
            ...viewerPanelSurfaceSx,
          }}
        >
          <Box sx={viewerPanelHeaderSx}>
            <Box sx={viewerPanelHeadingGroupSx}>
              <Typography variant="caption" sx={viewerPanelEyebrowSx}>
                Instruction
              </Typography>
            </Box>
          </Box>
          <Typography
            sx={{
              ...viewerPanelBodySx,
              mt: 0.65,
              overflowWrap: "anywhere",
              maxHeight: isPhone ? "7.8em" : "9.4em",
              overflowY: "auto",
            }}
          >
            {normalizedInstructionText}
          </Typography>
        </Box>
      )}

      {SHOW_GAME_STATE_PANEL && (
        <Box
          sx={{
            position: "absolute",
            top: `calc(var(--app-header-height, 0px) + 12px)`,
            right: "12px",
            color: "#fff",
            zIndex: 30,
            pointerEvents: "none",
            textAlign: "right",
            maxWidth: isPhone ? "62vw" : "380px",
            ...overlayPanelPaddingSx,
            ...viewerPanelSurfaceSx,
          }}
        >
          <Typography
            variant="caption"
            sx={{ ...mutedLabelSx, letterSpacing: "0.11em" }}
          >
            GAME STATE
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: '"area-extended", "area-normal", sans-serif',
              fontSize: isPhone ? "0.8rem" : "0.9rem",
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            {currentGameState}
          </Typography>
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: "1px solid rgba(255, 255, 255, 0.12)",
            }}
          >
            <Typography
              variant="caption"
              sx={{ ...mutedLabelSx, letterSpacing: "0.11em" }}
            >
              LAST UNITY ELEMENT
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: '"area-extended", "area-normal", sans-serif',
                fontSize: isPhone ? "0.8rem" : "0.9rem",
                lineHeight: 1.2,
                wordBreak: "break-word",
              }}
            >
              {lastUnitySelectedElementName || "None"}
            </Typography>
            {lastUnitySelectedElementId &&
              lastUnitySelectedElementId !== lastUnitySelectedElementName && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.35,
                    color: "rgba(236, 255, 251, 0.68)",
                    fontSize: isPhone ? "0.64rem" : "0.68rem",
                    lineHeight: 1.2,
                    wordBreak: "break-all",
                  }}
                >
                  {lastUnitySelectedElementId}
                </Typography>
              )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default StatusOverlays;
